<template>
  <SoDataTableServer
    :headers="userTableHeaders"
    :data="tableData"
    :options="tableOptions"
    search-placeholder="Search by email"
    searchable
    data-testId="admin-users-list-table"
    @fetch="fetchData"
  >
    <template #tableActions>
      <SoButton
        type="success"
        title="Create user"
        prepend-icon="plus"
        data-testid="create-new-user"
        class="ml-auto"
        large
        @click="isAdminAddUserModalOpen = true"
      />
    </template>
    <template #itemActions="{ item }">
      <div class="d-flex gap-1 justify-end">
        <SoButton
          title="Edit"
          type="table-action"
          data-testid="edit-user"
          :to="`/admin/user/${item.id}`"
        />

        <SoButton
          title="Get OTP"
          type="table-action"
          data-testid="user-get-otp"
          :loading="item.id === isFetchingOtpForUserId"
          :disabled="!!isFetchingOtpForUserId"
          @click="copyUserOtp(item.id)"
        />

        <SoButton
          v-if="!isDev"
          title="Sign In"
          type="table-action"
          data-testid="user-sign-in"
          icon="external-link"
          :loading="item.id === isSigningInUserId"
          :disabled="!!isSigningInUserId"
          @click="signInToMyAccount(item.id, item.email)"
        />
      </div>
    </template>
  </SoDataTableServer>
  <AdminAddUserModal v-model="isAdminAddUserModalOpen" />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { UserService, UserListResult } from "@/services/admin/UserService"
  import { useSnackbarStore } from "@/stores/snackbar"
  import SoDataTableServer, {
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
  } from "@/types/components/SoDataTable"
  import SoButton from "@/components/formControls/SoButton.vue"
  import AdminAddUserModal from "../modals/AdminAddUserModal.vue"
  import { dayjsOrNull } from "@/helpers/dateHelper"
  import { errorReporter } from "@/services/ErrorService"
  import { QueryResult } from "@/api/utils/types"

  const headers: SoDataTableHeader[] = [
    {
      title: "ID",
      key: "id",
    },
    {
      title: "Email",
      key: "email",
    },
    {
      title: "Locked Date",
      key: "locked",
      type: SoDataTableColumnType.DATE,
      fallbackValue: "Never",
    },
    {
      title: "Created At",
      key: "created_at",
      type: SoDataTableColumnType.DATE,
    },
    {
      title: "Sign In Count",
      key: "sign_in_count",
    },
    {
      title: "Current Sign In IP",
      key: "current_sign_in_ip",
      fallbackValue: "None",
    },
  ]

  export default defineComponent({
    components: {
      SoButton,
      SoDataTableServer,
      AdminAddUserModal,
    },
    data() {
      return {
        snackbar: useSnackbarStore(),
        userTableHeaders: headers,
        isFetchingOtpForUserId: null as number | null,
        isSigningInUserId: null as number | null,
        isAdminAddUserModalOpen: false,
        query: null as QueryResult<UserListResult> | null,
        tableOptions: {
          search: "" as string | null,
        } as Partial<SoDataTableOptions>,
      }
    },
    computed: {
      tableData() {
        return {
          isLoading: !!this.query?.isFetching,
          totalItems: this.query?.data?.users_aggregate.aggregate?.count ?? 0,
          items:
            this.query?.data?.users.map((user) => ({
              ...user,
              locked: dayjsOrNull(user.lockedAt),
              created_at: dayjsOrNull(user.createdAt),
              sign_in_count: user.signInCount,
              current_sign_in_ip: user.currentSignInIp,
            })) ?? [],
        }
      },
      isDev() {
        return !!import.meta.env.DEV
      },
    },
    created() {
      this.tableOptions.search = (this.$route.query.email ?? "") as string
    },
    methods: {
      fetchData({ search, page, pageSize, orderBy }: SoDataTableOptions) {
        this.$router.replace({
          query: {
            ...(search && { email: search }),
          },
        })

        this.query = UserService.getUserList(search, page, pageSize, orderBy)
      },
      async copyUserOtp(userId: number) {
        this.isFetchingOtpForUserId = userId
        try {
          await UserService.getOtp(userId, true)
          this.snackbar.addSuccess("OTP has been copied to your clipboard!")
        } catch (e) {
          errorReporter.report(e)
          console.error(e)
          this.snackbar.addError(
            "Sorry, we couldn't retrieve the OTP. Please try again.",
          )
        } finally {
          this.isFetchingOtpForUserId = null
        }
      },

      async signInToMyAccount(userId: number, email: string) {
        try {
          this.isSigningInUserId = userId
          await UserService.signInToMyAccount(userId, email)
        } catch (e) {
          console.error(e)
          this.snackbar.addError(
            "Sorry, we couldn't sign in to the user. Please try again.",
          )
        } finally {
          this.isSigningInUserId = null
        }
      },
    },
  })
</script>
